"use client";

import React, { useEffect } from "react";
import {
  AlertTriangle,
  Download,
  ExternalLink,
  FileText,
  FolderOpen,
  RefreshCw,
} from "lucide-react";

import type { Contracts } from "@/src/gql/graphql";
import type { ContractDocumentVariant } from "@/lib/services/esignature/contract-document-name";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useContractDocument } from "@/features/contracts/hooks/useContractDocument";

/**
 * "Document" tab — the raw agreement, inline.
 *
 * This is the tab a sales rep opens to answer "what did we actually send them?".
 * It renders our durable S3 archive (never the eSignatures link, which expires
 * after ~3 days), and states the execution status in a banner that cannot be
 * missed: an unsigned agreement mistaken for an executed one is the failure
 * mode this whole surface exists to prevent.
 */

/** Signed contracts can show both what was executed and what was originally sent. */
function variantOptions(status: string): ContractDocumentVariant[] {
  return status === "signed" ? ["signed", "unsigned"] : ["unsigned"];
}

const VARIANT_LABEL: Record<ContractDocumentVariant, string> = {
  signed: "Executed copy",
  unsigned: "As sent (unsigned)",
};

function ExecutionBanner({ variant }: { variant: ContractDocumentVariant }) {
  if (variant === "signed") {
    return (
      <Badge className="bg-green-100 text-green-800">Signed — executed agreement</Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-800">
      <AlertTriangle className="mr-1 h-3 w-3" />
      Unsigned — this is the draft as sent, not an executed agreement
    </Badge>
  );
}

export function ContractDocumentTab({ item: contract }: { item: Contracts }) {
  const contractId = typeof contract.id === "string" ? contract.id : null;
  const doc = useContractDocument(contractId);
  const { load } = doc;
  const options = variantOptions(contract.status);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="min-h-[400px] space-y-4">
      {/* Toolbar — state first, then the things a rep does with the file. */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-4">
        <div className="flex min-w-0 items-center gap-3">
          <FileText className="h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-card-foreground">
              {doc.displayName ||
                contract.document_name ||
                contract.template_name ||
                "Contract document"}
            </p>
            {doc.variant && (
              <div className="mt-1">
                <ExecutionBanner variant={doc.variant} />
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {options.length > 1 && (
            <div className="flex rounded-md border">
              {options.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => void load(option)}
                  className={`px-3 py-1.5 text-xs font-medium first:rounded-l-md last:rounded-r-md ${
                    doc.variant === option
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {VARIANT_LABEL[option]}
                </button>
              ))}
            </div>
          )}

          {doc.phase === "ready" && doc.url && (
            <>
              <Button asChild variant="outline" size="sm">
                <a href={doc.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                  Open in new tab
                </a>
              </Button>
              <Button asChild variant="outline" size="sm">
                <a href={doc.url} download={doc.filename ?? undefined}>
                  <Download className="h-4 w-4" />
                  Download
                </a>
              </Button>
            </>
          )}

          {doc.driveWebViewLink && (
            <Button asChild variant="outline" size="sm">
              <a href={doc.driveWebViewLink} target="_blank" rel="noopener noreferrer">
                <FolderOpen className="h-4 w-4" />
                Open in Drive
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Viewer */}
      <div className="h-[70vh] overflow-hidden rounded-lg border bg-muted/30">
        {doc.phase === "loading" && (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
            <p className="text-sm">Loading the document…</p>
          </div>
        )}

        {doc.phase === "generating" && (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
            <p className="text-sm">Rendering the agreement… this takes a few seconds.</p>
            {doc.elapsedSeconds >= 10 && (
              <p className="text-xs">Still working ({doc.elapsedSeconds}s) — almost there.</p>
            )}
          </div>
        )}

        {doc.phase === "ready" && doc.url && (
          <iframe
            src={doc.url}
            title={doc.displayName ?? "Contract document"}
            className="h-full w-full border-0"
          />
        )}

        {(doc.phase === "unavailable" || doc.phase === "error") && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <p className="max-w-md text-sm text-muted-foreground">
              {doc.message ?? "No copy of this document is available."}
            </p>
            {/* ERROR_STATES_ACTIONABLE: offer the fix here, not just the diagnosis. */}
            {doc.canGenerate && (
              <Button size="sm" onClick={() => void doc.generate()}>
                <RefreshCw className="h-4 w-4" />
                Fetch the document
              </Button>
            )}
            {!doc.canGenerate && doc.phase === "error" && (
              <Button size="sm" variant="outline" onClick={() => void load(doc.variant ?? undefined)}>
                <RefreshCw className="h-4 w-4" />
                Try again
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
